import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, Typography, Button, Input, useToast, Alert as SpeculoAlert, AlertDescription, Checkbox } from '@crisellevaldez/speculo-ui';
import { RolloutLinkProvider, CredentialsManager } from '@rollout/link-react';
// Import CSS URL for dynamic loading (using Vite's ?url suffix)
import rolloutCssUrl from '@rollout/link-react/style.css?url';
import '../../../styles/rollout-overrides.css';
import { Organization } from '../../../types/organization.types';
import {
  MessageSquare,
  RotateCw,
  Edit,
  // Database,
  Code,
  // Radio,
  Settings,
  // LucideFileWarning,
  // Tag,
  Link,
  Check,
  X,
  AlertTriangle,
} from 'lucide-react';
import { RolloutSyncStatusBanner, RolloutSyncResults } from './RolloutSyncStatusBanner';
import { formatPhoneNumber } from '../../../utils/formatters';
import { TwilioModal } from './TwilioModal';
import { CRMModal } from './CRMModal';
import WebsiteIntegrationModal from './WebsiteIntegrationModal';
import { CINCModal } from './CINCModal';
import { SpeculoOAuthModal } from './SpeculoOAuthModal';
import { getOAuthClientInfo } from '../../../api/speculoOAuth';
import { FubStagesModal } from './FubStagesModal';
import { TipBox } from '../onboarding/components/TipBox';
import { useUser } from '../../../hooks/useUser';
import { useAuthStore } from '../../../contexts/auth/useAuthStore';
import { isProductionTestCrmOrg, PRODUCTION_TEST_CRMS } from '../../../api/productionTestCrmReads';
import { ExistingProductionCrmBinding } from './ExistingProductionCrmBinding';
import { getProductionCrmConnectionStatus, isManagedCrmApp, type ProductionCrmConnectionStatus } from '../../../api/productionCrmConnections';
import { AdminLockOverlay } from './AdminLockOverlay';
import { useRolloutToken } from '../../../hooks/rollout/useRolloutToken';
import { useRolloutCrmConnection } from '../../../hooks/rollout/useRolloutCrmConnection';
import { validateSubdomain } from '../../../schemas/subdomain.schema';
import { useUpdateOrganizationSettings } from '../../../hooks/organizations/useUpdateOrganizationSettings';
import { useRolloutStages } from '../../../hooks/rollout/useRolloutStages';
import { useIntegrationHealth } from '../../../hooks/integrations/useIntegrationHealth';
import { NON_OWNER_KEY_WARNING } from '../../../api/crm';
import { ConfirmationDialog } from '../../common/dialogs/ConfirmationDialog';
import {
  hasAnyConnectedCrm,
  isCrmConnectedViaRollout,
} from '../../../utils/rollout';
import { SharedFubAccountSetup } from './SharedFubAccountSetup';
import {
  ALL_ROLLOUT_CRM_APP_KEYS,
  CRM_TRADEMARK_NOTICE,
  MANUAL_CONNECT_CRMS,
  MANUAL_CONNECT_CRM_IDS,
  ManualConnectCrmId,
  ROLLOUT_CRMS,
  RolloutCrmId,
} from '../../../constants/rolloutCrms';
import { getCrmConnectRequest } from '../../../utils/manualCrmConnect';
import { ManualCrmConnectModal } from './ManualCrmConnectModal';
import { getUatLoftyDisabledStatus, getUatSavedCredentialDisabledStatus, inspectUatConnector, inspectUatExactLoftyPerson, inspectUatExistingLofty, inspectUatSavedCredential, inspectUatSavedCredentialCandidate, installUatLoftyDisabledBinding, installUatSavedCredentialDisabledBinding, UatCrmInspection, UatLoftyDisabledStatus, UatLoftyExactPersonInspection, UatSavedCredentialCandidateInspection, UatSavedCredentialDisabledStatus, UatSavedCredentialOrgId } from '../../../api/uatCrmInspection';
import { PROTECTED_CRM_APPS, classifyCredentialCallback, isCurrentProtectedContext, readCurrentInspection } from '../../../utils/uatCrmInspection';

interface IntegrationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  /** Current row and fresh detail IDs; cached display data never authorizes inspection. */
  selectedOrgId?: string;
  currentRowId?: string;
  freshDetailLoading?: boolean;
  onboardingConsentAcknowledged?: boolean;
  freshDetailId?: string;
  onSuccess?: () => void;
  hideTwilio?: boolean;
  /** When true, only shows FUB integration (for onboarding). Legacy — prefer crmFilter. */
  fubOnly?: boolean;
  /** Restrict the Rollout connector list to a single CRM (onboarding per-CRM flow) */
  crmFilter?: RolloutCrmId;
  /** Require AI acknowledgment before showing Rollout connect controls */
  requireAIAcknowledgement?: boolean;
  /** Optional custom copy for AI acknowledgment checkbox */
  aiAcknowledgementCopy?: string;
}

let nextRolloutAuthEpoch = 0;

const OrdinaryIntegrationsModal: React.FC<IntegrationsModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  selectedOrgId: currentSelectedOrgId,
  freshDetailLoading,
  onboardingConsentAcknowledged = false,
  freshDetailId: freshSelectedOrgId,
  onSuccess,
  hideTwilio = false,
  fubOnly = false,
  crmFilter,
  requireAIAcknowledgement = true,
  aiAcknowledgementCopy = 'By checking the box and connecting your CRM and/or submitting data, you acknowledge and agree that you are solely responsible for ensuring that any contacts made available for outreach have provided appropriate consent to be contacted via automated systems, including AI-powered voice calls, dialing technology, text messaging, and email communications.\n\nYou further confirm that you have implemented appropriate tagging, filtering, and/or segmentation within your CRM to identify such contacts, and that only eligible contacts will be engaged by the system in compliance with all applicable laws, regulations, and communication consent requirements.',
}) => {
  // Derived filter: explicit crmFilter, else fubOnly → fub, else no restriction
  const effectiveCrmFilter: RolloutCrmId | undefined = crmFilter || (fubOnly ? 'fub' : undefined);
  const restricted = Boolean(effectiveCrmFilter);

  // Get user role to determine permissions
  const { user, isAdmin, isSuperAdmin } = useUser();
  const { user: authUser, loading: authLoading } = useAuthStore();
  const authRole = user?.role ?? null;
  const authEpoch = useRef<{user: typeof authUser; role: string | null;
    admin: boolean; superadmin: boolean; loading: boolean; count: number} | null>(null);
  if (!authEpoch.current || authEpoch.current.user !== authUser ||
      authEpoch.current.role !== authRole || authEpoch.current.admin !== isAdmin ||
      authEpoch.current.superadmin !== isSuperAdmin || authEpoch.current.loading !== authLoading) {
    authEpoch.current = {user: authUser, role: authRole, admin: isAdmin,
      superadmin: isSuperAdmin, loading: authLoading, count: ++nextRolloutAuthEpoch};
  }
  const selectedContextReady = currentSelectedOrgId === undefined ||
    (selectedOrg?.id === currentSelectedOrgId &&
      freshSelectedOrgId === currentSelectedOrgId && freshDetailLoading === false);
  const widgetReady = isOpen && selectedContextReady && Boolean(user?.id) &&
    Boolean(authUser?.uid) && !authLoading && (isAdmin || isSuperAdmin);
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const protectedApp = import.meta.env.VITE_APP_ENV === 'uat' && selectedOrg?.id
    ? PROTECTED_CRM_APPS[selectedOrg.id] : undefined;
  const protectedContextReady = isCurrentProtectedContext({
    isOpen, environment: import.meta.env.VITE_APP_ENV ?? '', isSuperAdmin,
    selectedOrgId: selectedOrg?.id, currentSelectedOrgId, freshSelectedOrgId,
    active: selectedOrg?.active,
  }) && (!crmFilter ||
    (crmFilter === 'lofty' && protectedApp === 'lofty') ||
    (crmFilter === 'sierra_interactive' && protectedApp === 'sierra-interactive') ||
    (crmFilter === 'boldtrail' && protectedApp === 'boldtrail'));
  const savedCredentialOrgId: UatSavedCredentialOrgId | undefined = protectedContextReady &&
    (selectedOrg?.id === '6e5adb4f-c2b4-459b-8c40-f41bb801bbf2' ||
     selectedOrg?.id === '048164c9-68a7-4f2c-ac30-3400e3fa47e7')
      ? selectedOrg.id : undefined;
  // Change generations synchronously during render, so a late SDK callback or
  // metadata request from a previous org/close/session cannot act on new props.
  const contextIdentity = [isOpen, selectedOrg?.id, currentSelectedOrgId,
    freshSelectedOrgId, selectedOrg?.active, isSuperAdmin, user?.id,
    protectedApp, crmFilter, freshDetailLoading, authUser?.uid,
    authLoading, authEpoch.current.count, isAdmin].join('|');
  const contextRef = useRef({ identity: contextIdentity, generation: 0 });
  if (contextRef.current.identity !== contextIdentity) {
    contextRef.current = { identity: contextIdentity, generation: contextRef.current.generation + 1 };
  }
  const generation = contextRef.current.generation;
  const [inspection, setInspection] = useState<{
    generation: number; phase: 'loading' | 'ready' | 'saved' | 'error'; data?: UatCrmInspection;
  } | null>(null);
  const currentInspection = inspection?.generation === generation ? inspection : null;
  const inspectionInFlightRef = useRef<number | null>(null);
  const [existingLoftyInspection, setExistingLoftyInspection] = useState<{
    generation: number; phase: 'loading' | 'ready' | 'error'; data?: UatCrmInspection;
  } | null>(null);
  const currentExistingLoftyInspection = existingLoftyInspection?.generation === generation
    ? existingLoftyInspection : null;
  const existingLoftyInFlightRef = useRef<number | null>(null);
  const [loftyPerson, setLoftyPerson] = useState<{
    generation: number; phase: 'loading' | 'ready' | 'error'; data?: UatLoftyExactPersonInspection;
  } | null>(null);
  const [loftyStatus, setLoftyStatus] = useState<{
    generation: number; phase: 'loading' | 'ready' | 'error'; data?: UatLoftyDisabledStatus;
  } | null>(null);
  const [loftyInstallPhase, setLoftyInstallPhase] = useState<{
    generation: number; phase: 'loading' | 'error';
  } | null>(null);
  const currentLoftyPerson = loftyPerson?.generation === generation ? loftyPerson : null;
  const currentLoftyStatus = loftyStatus?.generation === generation ? loftyStatus : null;
  const currentLoftyInstallPhase = loftyInstallPhase?.generation === generation ? loftyInstallPhase : null;
  const loftyActionInFlightRef = useRef<number | null>(null);
  const [savedCredentialStatus, setSavedCredentialStatus] = useState<{
    generation: number; phase: 'loading' | 'ready' | 'error'; data?: UatSavedCredentialDisabledStatus;
  } | null>(null);
  const [savedCredentialCandidate, setSavedCredentialCandidate] = useState<{
    generation: number; phase: 'loading' | 'ready' | 'error'; data?: UatSavedCredentialCandidateInspection;
  } | null>(null);
  const [savedCredentialInstallPhase, setSavedCredentialInstallPhase] = useState<{
    generation: number; phase: 'loading' | 'error';
  } | null>(null);
  const currentSavedStatus = savedCredentialStatus?.generation === generation ? savedCredentialStatus : null;
  const currentSavedCandidate = savedCredentialCandidate?.generation === generation ? savedCredentialCandidate : null;
  const currentSavedInstallPhase = savedCredentialInstallPhase?.generation === generation ? savedCredentialInstallPhase : null;
  const savedCredentialActionInFlightRef = useRef<number | null>(null);
  const mountedRef = useRef(false);
  useEffect(() => {
    setInspection(null); setExistingLoftyInspection(null);
    setLoftyPerson(null); setLoftyStatus(null); setLoftyInstallPhase(null);
    setSavedCredentialStatus(null); setSavedCredentialCandidate(null); setSavedCredentialInstallPhase(null);
  }, [generation]);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Modal states for each integration
  const [isTwilioModalOpen, setIsTwilioModalOpen] = useState(false);
  const [isCRMModalOpen, setIsCRMModalOpen] = useState(false);
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
  const [isCINCModalOpen, setIsCINCModalOpen] = useState(false);
  const [isOAuthModalOpen, setIsOAuthModalOpen] = useState(false);
  const [isStagesModalOpen, setIsStagesModalOpen] = useState(false);
  const [showConfirmLegacyDisconnect, setShowConfirmLegacyDisconnect] = useState(false);
  const [isDisconnectingLegacy, setIsDisconnectingLegacy] = useState(false);
  const persistedPendingSharedParentId =
    selectedOrg?.crmConnections?.find(
      (connection) =>
        connection.app === 'follow-up-boss' && connection.status === 'pending_setup'
    )?.connectionId ?? null;
  const persistedFubConnectionMode = persistedPendingSharedParentId
    ? 'shared_account'
    : selectedOrg?.rollout?.fub?.connectionMode ?? 'single_org';
  const [fubConnectionMode, setFubConnectionMode] = useState<'single_org' | 'shared_account'>(
    persistedFubConnectionMode
  );
  const [localSharedParentId, setLocalSharedParentId] = useState<string | null>(null);
  // Auto-acknowledge if org already consented (in onboardingData) OR already
  // has any connected CRM via Rollout. Accept legacy camelCase
  // `crmConsentAcknowledged` from old Firestore onboarding.
  const onboardingData = selectedOrg?.onboardingData as
    | { crm_consent_acknowledged?: boolean; crmConsentAcknowledged?: boolean }
    | undefined;
  const baseOnboardingData = selectedOrg?.onboardingData ?? {};
  const hasExistingConsent = Boolean(
    onboardingData?.crm_consent_acknowledged ||
      onboardingData?.crmConsentAcknowledged ||
      hasAnyConnectedCrm({
        crmConnections: selectedOrg?.crmConnections,
        rollout: selectedOrg?.rollout,
      })
  );
  const [hasAcknowledgedAI, setHasAcknowledgedAI] = useState(hasExistingConsent);

  // FUB subdomain state - canonical home is `fubSetup.subdomain`; legacy
  // `rollout.fub.subdomain` is read as a fallback for orgs still on the old shape.
  const fubSubdomainPersisted =
    selectedOrg?.fubSetup?.subdomain || selectedOrg?.rollout?.fub?.subdomain || '';
  const [fubSubdomain, setFubSubdomain] = useState(fubSubdomainPersisted);
  const [isEditingSubdomain, setIsEditingSubdomain] = useState(false);

  // Track local disconnect so stages hide immediately without waiting for org refresh
  const [didDisconnectFub, setDidDisconnectFub] = useState(false);

  // Reset disconnect flag when org changes
  useEffect(() => {
    setDidDisconnectFub(false);
    setFubConnectionMode(persistedFubConnectionMode);
    setLocalSharedParentId(null);
  }, [selectedOrg?.id, persistedFubConnectionMode]);

  // Sync local state whenever the persisted consent flag changes — covers
  // selectedOrg loading after mount and the consent being saved via the
  // checkbox handler below.
  useEffect(() => {
    setHasAcknowledgedAI(hasExistingConsent);
  }, [hasExistingConsent]);

  // Super admins should never be presented with the client-consent gate —
  // they're approving on behalf of someone else and shouldn't be able to
  // accidentally accept the contact-consent declaration for the org.
  const showConsentGate = requireAIAcknowledgement && !isSuperAdmin && !hasAcknowledgedAI;

  // Check if FUB is connected via Rollout — handles new credentials[] shape
  // and legacy rollout.fub.connected shape.
  const isFubConnected =
    isCrmConnectedViaRollout(
      { crmConnections: selectedOrg?.crmConnections, rollout: selectedOrg?.rollout },
      'follow-up-boss'
    ) && !didDisconnectFub;
  const isSharedFubMode = fubConnectionMode === 'shared_account';
  // CINC, Lofty, BoldTrail and Sierra Interactive connect through Speculo's
  // Connect form. Orgs already connected to one through Rollout keep its tile.
  const crmSources = { crmConnections: selectedOrg?.crmConnections, rollout: selectedOrg?.rollout };
  const isManualCrmConnectedViaRollout = (crm: ManualConnectCrmId) =>
    ROLLOUT_CRMS[crm].appKeys.some((appKey) => isCrmConnectedViaRollout(crmSources, appKey));
  const connectedManualCrmAppKeys: string[] = MANUAL_CONNECT_CRM_IDS.filter(
    isManualCrmConnectedViaRollout
  ).flatMap((crm) => [...ROLLOUT_CRMS[crm].appKeys]);
  const [manualConnectCrm, setManualConnectCrm] = useState<ManualConnectCrmId | null>(null);
  // Super admins connect these CRMs in Rollout itself; everyone else uses the Connect form.
  const showManualCrmTiles = !restricted && !isSuperAdmin;
  const rolloutManualCrmAppKeys: string[] = isSuperAdmin
    ? [
        ...MANUAL_CONNECT_CRM_IDS.map((crm) => ROLLOUT_CRMS[crm].appKeys[0]),
        ...connectedManualCrmAppKeys,
      ]
    : connectedManualCrmAppKeys;
  const rolloutConnectorAppKeys: readonly string[] = (effectiveCrmFilter
    ? ROLLOUT_CRMS[effectiveCrmFilter].appKeys
    : [...ALL_ROLLOUT_CRM_APP_KEYS, ...rolloutManualCrmAppKeys])
    .filter((app) => !['cinc-pro', 'boldtrail-oauth'].includes(app));
  // Rollout connector the user has open in the Connect list (null on the list).
  // Super admins choose the FUB account structure only while the Follow Up Boss
  // connector is open, so the choice doesn't sit above every other CRM.
  const [openRolloutConnector, setOpenRolloutConnector] = useState<string | null>(null);
  // Rollout reopens on its connector list, so forget the open connector when the
  // popup closes (however it's closed). Adjusting state during render avoids an effect.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen) setOpenRolloutConnector(null);
  }
  const showFubAccountStructure =
    isSuperAdmin &&
    !isFubConnected &&
    ROLLOUT_CRMS.fub.appKeys.some((appKey) => appKey === openRolloutConnector);

  // CredentialsManager doesn't expose which connector is open, so read it from
  // its markup: a list tile's icon URL ends in /connectors/<appKey>/icon, and the
  // connector view has a "Go Back to Integrations" button. Revisit if
  // @rollout/link-react changes this markup.
  const trackRolloutConnector = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    const tile = target.closest('.universal-ConnectorListItem-root');
    if (tile) {
      const iconSrc = tile.querySelector('img')?.getAttribute('src') ?? '';
      setOpenRolloutConnector(iconSrc.match(/\/connectors\/([^/]+)\/icon/)?.[1] ?? null);
    } else if (target.closest('[aria-label="Go Back to Integrations"]')) {
      setOpenRolloutConnector(null);
    }
  };
  const sharedParentConnectionId = localSharedParentId ?? persistedPendingSharedParentId ?? (
    isSharedFubMode
      ? selectedOrg?.crmConnections?.find(
          (connection) =>
            connection.app === 'follow-up-boss' &&
            (connection.status === 'pending_setup' || connection.status === 'connected')
        )?.connectionId ?? null
      : null
  );

  const syncStatus = selectedOrg?.last_initial_sync;

  // Owner-account signal for the connected FUB key. Webhook sync only works
  // with an owner-level API key, so warn when the connected key isn't one.
  const { data: integrationHealth } = useIntegrationHealth({
    organizationId: selectedOrg?.id,
    enabled: isOpen && isFubConnected && !isSharedFubMode,
  });
  const isNonOwnerKey = integrationHealth?.owner_account_status === 'non_owner_key';

  // Prefer the FUB connectionId from the typed crmConnections list (new API).
  // Fall back to orgId — the backend still resolves that to the active
  // connection for orgs whose GQL hasn't repopulated crmConnections yet.
  const fubConnectionId =
    selectedOrg?.crmConnections?.find(
      (c) => c.app === 'follow-up-boss' && (c.status === 'active' || c.status === 'connected')
    )?.connectionId ?? selectedOrg?.id;

  // Stages are sourced from the API (single source of truth) so that the
  // optimistic update in useRolloutStages can reflect a save instantly without
  // waiting for the GQL org refetch.
  const { stages: fubStages, isLoading: isLoadingFubStages } = useRolloutStages({
    orgId: fubConnectionId,
    enabled: isOpen && isFubConnected,
  });
  const enabledStagesCount = fubStages.filter((s) => s.enabled).length;
  const isSyncing = syncStatus?.status === 'processing';

  // Check if legacy FUB is connected (via API key)
  const isLegacyFubConnected = Boolean(selectedOrg?.crmApiKeyHash);

  // Dynamically load/unload Rollout CSS only when modal is open to prevent global style leaks
  useEffect(() => {
    if (isOpen) {
      // Check if CSS is already loaded
      const existingLink = document.getElementById('rollout-css');
      if (!existingLink) {
        const linkElement = document.createElement('link');
        linkElement.id = 'rollout-css';
        linkElement.rel = 'stylesheet';
        linkElement.href = rolloutCssUrl;
        document.head.appendChild(linkElement);
      }
    } else {
      // Cleanup: remove the CSS when modal closes
      const existingLink = document.getElementById('rollout-css');
      if (existingLink) {
        existingLink.remove();
      }
    }
  }, [isOpen]);

  // Rollout token for Follow Up Boss - using React Query (no useEffect needed!)
  const {
    data: rolloutToken,
    isLoading: tokenLoading,
    error: tokenError,
  } = useRolloutToken({
    orgId: selectedOrg?.id,
    authContext: {uid: authUser?.uid, userId: user?.id,
      sessionEpoch: authEpoch.current.count, role: authRole},
    enabled: widgetReady, // Authenticated current context only
  });

  // CRM connection handlers - saves state to Firestore (rollout.<crm>.connected)
  const { handleCredentialAdded: baseHandleCredentialAdded, handleCredentialDeleted, isConnecting, isDisconnecting } =
    useRolloutCrmConnection({
      selectedOrg,
      onSuccess,
      connectionMode: fubConnectionMode,
      isCurrent: () => mountedRef.current && generation === contextRef.current.generation && widgetReady,
    });

  const { updateSettings, isPending: isSavingSubdomainViaApi } = useUpdateOrganizationSettings(onSuccess);
  // Consent persistence must not trigger the parent refetch mid-callback.
  const { updateSettings: persistCrmConsent } = useUpdateOrganizationSettings();

  const inspectProtectedConnector = async () => {
    if (!protectedApp || !protectedContextReady || !selectedOrg?.id ||
        inspectionInFlightRef.current === generation) return;
    const initiatedGeneration = generation;
    const orgId = selectedOrg.id;
    inspectionInFlightRef.current = initiatedGeneration;
    setInspection({ generation: initiatedGeneration, phase: 'loading' });
    try {
      const data = await readCurrentInspection({
        orgId, app: protectedApp,
        isCurrent: () => mountedRef.current && initiatedGeneration === contextRef.current.generation,
        request: () => inspectUatConnector(orgId),
      });
      if (data) setInspection({ generation: initiatedGeneration, phase: 'ready', data });
    } catch {
      if (mountedRef.current && initiatedGeneration === contextRef.current.generation) {
        setInspection({ generation: initiatedGeneration, phase: 'error' });
      }
    } finally {
      if (inspectionInFlightRef.current === initiatedGeneration) inspectionInFlightRef.current = null;
    }
  };

  const inspectExistingLofty = async () => {
    if (protectedApp !== 'lofty' || !protectedContextReady ||
        selectedOrg?.id !== '816d1e20-f38b-466e-96ec-46bb7d0f404c' ||
        existingLoftyInFlightRef.current === generation) return;
    const initiatedGeneration = generation;
    const orgId = selectedOrg.id;
    existingLoftyInFlightRef.current = initiatedGeneration;
    setExistingLoftyInspection({ generation: initiatedGeneration, phase: 'loading' });
    try {
      const data = await readCurrentInspection({
        orgId, app: 'lofty',
        isCurrent: () => mountedRef.current && initiatedGeneration === contextRef.current.generation,
        request: inspectUatExistingLofty,
      });
      if (data) {
        if (data.metadata.credential_present !== true || data.metadata.credential_valid !== true) {
          throw new Error('Existing credential unavailable');
        }
        setExistingLoftyInspection({ generation: initiatedGeneration, phase: 'ready', data });
      }
    } catch {
      if (mountedRef.current && initiatedGeneration === contextRef.current.generation) {
        setExistingLoftyInspection({ generation: initiatedGeneration, phase: 'error' });
      }
    } finally {
      if (existingLoftyInFlightRef.current === initiatedGeneration) existingLoftyInFlightRef.current = null;
    }
  };

  const currentLoftyContext = () => protectedApp === 'lofty' && protectedContextReady &&
    selectedOrg?.id === '816d1e20-f38b-466e-96ec-46bb7d0f404c';

  const currentLoftyAction = () => currentLoftyContext() &&
    currentExistingLoftyInspection?.phase === 'ready' &&
    currentExistingLoftyInspection.data?.metadata.credential_present === true &&
    currentExistingLoftyInspection.data?.metadata.credential_valid === true;

  const currentLoftyPersonRead = () => currentLoftyContext() &&
    (currentLoftyAction() || (currentLoftyStatus?.phase === 'ready' &&
      currentLoftyStatus.data?.status === 'credential_bound_disabled'));

  const inspectExactLoftyPerson = async () => {
    if (!currentLoftyPersonRead() || loftyActionInFlightRef.current === generation) return;
    const initiatedGeneration = generation;
    loftyActionInFlightRef.current = initiatedGeneration;
    setLoftyPerson({ generation: initiatedGeneration, phase: 'loading' });
    try {
      const data = await inspectUatExactLoftyPerson();
      if (mountedRef.current && initiatedGeneration === contextRef.current.generation) {
        if (data.status !== 'exact_fixture_read' || data.writes !== 0 ||
            data.provider_account_qualified !== false || data.import_enabled !== false) {
          throw new Error('Unexpected exact-person inspection');
        }
        setLoftyPerson({ generation: initiatedGeneration, phase: 'ready', data });
      }
    } catch {
      if (mountedRef.current && initiatedGeneration === contextRef.current.generation) {
        setLoftyPerson({ generation: initiatedGeneration, phase: 'error' });
      }
    } finally {
      if (loftyActionInFlightRef.current === initiatedGeneration) loftyActionInFlightRef.current = null;
    }
  };

  const readLoftyDisabledStatus = async () => {
    if (!currentLoftyContext() || loftyActionInFlightRef.current === generation) return;
    const initiatedGeneration = generation;
    loftyActionInFlightRef.current = initiatedGeneration;
    setLoftyPerson(null);
    setLoftyStatus({ generation: initiatedGeneration, phase: 'loading' });
    try {
      const data = await getUatLoftyDisabledStatus();
      if (mountedRef.current && initiatedGeneration === contextRef.current.generation) {
        if (!['not_installed', 'credential_bound_disabled'].includes(data.status) ||
            typeof data.install_admitted !== 'boolean' || data.writes !== 0 ||
            typeof data.plan !== 'string' || !data.plan || data.plan.length > 100 ||
            typeof data.auth_type !== 'string' || !data.auth_type || data.auth_type.length > 100 ||
            data.provider_account_qualified !== false || data.import_enabled !== false ||
            data.outreach_enabled !== false) throw new Error('Unexpected Lofty status');
        setLoftyStatus({ generation: initiatedGeneration, phase: 'ready', data });
      }
    } catch {
      if (mountedRef.current && initiatedGeneration === contextRef.current.generation) {
        setLoftyStatus({ generation: initiatedGeneration, phase: 'error' });
      }
    } finally {
      if (loftyActionInFlightRef.current === initiatedGeneration) loftyActionInFlightRef.current = null;
    }
  };

  const installLoftyDisabled = async () => {
    if (!currentLoftyAction() || currentLoftyStatus?.phase !== 'ready' ||
        currentLoftyStatus.data?.install_admitted !== true ||
        currentLoftyStatus.data.status !== 'not_installed' ||
        loftyActionInFlightRef.current === generation) return;
    const initiatedGeneration = generation;
    loftyActionInFlightRef.current = initiatedGeneration;
    setLoftyInstallPhase({ generation: initiatedGeneration, phase: 'loading' });
    setLoftyStatus({ generation: initiatedGeneration, phase: 'loading' });
    try {
      await installUatLoftyDisabledBinding();
      if (!mountedRef.current || initiatedGeneration !== contextRef.current.generation) return;
      const data = await getUatLoftyDisabledStatus();
      if (!mountedRef.current || initiatedGeneration !== contextRef.current.generation) return;
      if (data.status !== 'credential_bound_disabled' || data.writes !== 0 ||
          data.provider_account_qualified !== false || data.import_enabled !== false ||
          data.outreach_enabled !== false) throw new Error('Disabled binding not confirmed');
      setLoftyStatus({ generation: initiatedGeneration, phase: 'ready', data });
      setLoftyInstallPhase(null);
    } catch {
      if (mountedRef.current && initiatedGeneration === contextRef.current.generation) {
        setLoftyInstallPhase({ generation: initiatedGeneration, phase: 'error' });
        setLoftyStatus({ generation: initiatedGeneration, phase: 'error' });
      }
    } finally {
      if (loftyActionInFlightRef.current === initiatedGeneration) loftyActionInFlightRef.current = null;
    }
  };

  const savedStatusIsCurrent = () => {
    const data = currentSavedStatus?.data;
    return Boolean(savedCredentialOrgId && currentSavedStatus?.phase === 'ready' && data &&
      data.app_key === protectedApp && data.status !== 'not_saved' &&
      typeof data.plan === 'string' && typeof data.auth_type === 'string');
  };

  const readSavedCredentialStatus = async () => {
    if (!savedCredentialOrgId || savedCredentialActionInFlightRef.current === generation) return;
    const initiatedGeneration = generation;
    const orgId = savedCredentialOrgId;
    const app = protectedApp;
    savedCredentialActionInFlightRef.current = initiatedGeneration;
    setSavedCredentialStatus({ generation: initiatedGeneration, phase: 'loading' });
    try {
      const data = await getUatSavedCredentialDisabledStatus(orgId);
      if (!mountedRef.current || initiatedGeneration !== contextRef.current.generation) return;
      if (data.app_key !== app || !['not_saved', 'not_installed', 'credential_bound_disabled'].includes(data.status) ||
          typeof data.install_admitted !== 'boolean' || data.writes !== 0 ||
          data.provider_account_qualified !== false || data.import_enabled !== false ||
          data.outreach_enabled !== false ||
          (data.status === 'not_saved'
            ? data.plan !== null || data.auth_type !== null
            : typeof data.plan !== 'string' || !data.plan || data.plan.length > 100 ||
              !['api_token', 'oauth1', 'oauth2'].includes(data.auth_type ?? ''))) {
        throw new Error('Unexpected saved credential status');
      }
      setSavedCredentialStatus({ generation: initiatedGeneration, phase: 'ready', data });
      setSavedCredentialCandidate(null);
      setSavedCredentialInstallPhase(null);
    } catch {
      if (mountedRef.current && initiatedGeneration === contextRef.current.generation) {
        setSavedCredentialStatus({ generation: initiatedGeneration, phase: 'error' });
      }
    } finally {
      if (savedCredentialActionInFlightRef.current === initiatedGeneration) {
        savedCredentialActionInFlightRef.current = null;
      }
    }
  };

  const readSavedCredentialCandidate = async () => {
    if (!savedStatusIsCurrent() || !savedCredentialOrgId ||
        savedCredentialActionInFlightRef.current === generation) return;
    const initiatedGeneration = generation;
    const orgId = savedCredentialOrgId;
    savedCredentialActionInFlightRef.current = initiatedGeneration;
    setSavedCredentialCandidate({ generation: initiatedGeneration, phase: 'loading' });
    try {
      const data = await inspectUatSavedCredentialCandidate(orgId);
      if (!mountedRef.current || initiatedGeneration !== contextRef.current.generation) return;
      if (data.status !== 'candidate_read_verified' || data.candidate_id_match !== true ||
          data.synthetic_shape_match !== true || typeof data.credential_bound !== 'boolean' ||
          data.provider_account_qualified !== false || data.import_enabled !== false ||
          data.outreach_enabled !== false || data.writes !== 0) {
        throw new Error('Unexpected candidate inspection');
      }
      setSavedCredentialCandidate({ generation: initiatedGeneration, phase: 'ready', data });
    } catch {
      if (mountedRef.current && initiatedGeneration === contextRef.current.generation) {
        setSavedCredentialCandidate({ generation: initiatedGeneration, phase: 'error' });
      }
    } finally {
      if (savedCredentialActionInFlightRef.current === initiatedGeneration) {
        savedCredentialActionInFlightRef.current = null;
      }
    }
  };

  const installSavedCredentialDisabled = async () => {
    if (!savedStatusIsCurrent() || !savedCredentialOrgId ||
        currentSavedStatus?.data?.status !== 'not_installed' ||
        currentSavedStatus.data.install_admitted !== true ||
        savedCredentialActionInFlightRef.current === generation) return;
    const initiatedGeneration = generation;
    const orgId = savedCredentialOrgId;
    const approved = currentSavedStatus.data;
    savedCredentialActionInFlightRef.current = initiatedGeneration;
    setSavedCredentialInstallPhase({ generation: initiatedGeneration, phase: 'loading' });
    setSavedCredentialStatus({ generation: initiatedGeneration, phase: 'loading' });
    try {
      await installUatSavedCredentialDisabledBinding(orgId);
      if (!mountedRef.current || initiatedGeneration !== contextRef.current.generation) return;
      const data = await getUatSavedCredentialDisabledStatus(orgId);
      if (!mountedRef.current || initiatedGeneration !== contextRef.current.generation) return;
      if (data.status !== 'credential_bound_disabled' || data.app_key !== approved.app_key ||
          data.plan !== approved.plan || data.auth_type !== approved.auth_type ||
          data.writes !== 0 || data.provider_account_qualified !== false ||
          data.import_enabled !== false || data.outreach_enabled !== false) {
        throw new Error('Disabled binding not confirmed');
      }
      setSavedCredentialStatus({ generation: initiatedGeneration, phase: 'ready', data });
      setSavedCredentialInstallPhase(null);
    } catch {
      if (mountedRef.current && initiatedGeneration === contextRef.current.generation) {
        setSavedCredentialInstallPhase({ generation: initiatedGeneration, phase: 'error' });
        setSavedCredentialStatus({ generation: initiatedGeneration, phase: 'error' });
      }
    } finally {
      if (savedCredentialActionInFlightRef.current === initiatedGeneration) {
        savedCredentialActionInFlightRef.current = null;
      }
    }
  };

  const showManagedSyncDirection = import.meta.env.VITE_APP_ENV === 'production' &&
    effectiveCrmFilter !== 'fub' && effectiveCrmFilter !== 'brivity';
  const [crmStatus, setCrmStatus] = useState<{
    generation: number;
    phase: 'loading' | 'ready' | 'error';
    data?: ProductionCrmConnectionStatus;
  } | null>(null);
  const crmStatusRequest = useRef(0);
  const shownCrmStatus = crmStatus?.generation === generation ? crmStatus : null;
  const currentManagedCrm = shownCrmStatus?.phase === 'ready' &&
    shownCrmStatus.data?.app && isManagedCrmApp(shownCrmStatus.data.app) &&
    (!effectiveCrmFilter || (ROLLOUT_CRMS[effectiveCrmFilter].appKeys as readonly string[])
      .includes(shownCrmStatus.data.app))
    ? shownCrmStatus.data : null;

  useEffect(() => {
    if (!widgetReady || !showManagedSyncDirection || !selectedOrg?.id) return;
    const request = ++crmStatusRequest.current;
    let cancelled = false;
    setCrmStatus({ generation, phase: 'loading' });
    void getProductionCrmConnectionStatus(selectedOrg.id).then((data) => {
      if (!cancelled && contextRef.current.generation === generation &&
          crmStatusRequest.current === request) {
        setCrmStatus({ generation, phase: 'ready', data });
      }
    }).catch(() => {
      if (!cancelled && contextRef.current.generation === generation &&
          crmStatusRequest.current === request) {
        setCrmStatus({ generation, phase: 'error' });
      }
    });
    return () => { cancelled = true; };
  }, [widgetReady, showManagedSyncDirection, selectedOrg?.id, generation]);

  const refreshManagedCrmStatus = async (app: string, started: number) => {
    if (!showManagedSyncDirection || !isManagedCrmApp(app) || !selectedOrg?.id ||
        !mountedRef.current || started !== contextRef.current.generation || !widgetReady) return;
    const request = ++crmStatusRequest.current;
    setCrmStatus({generation: started, phase: 'loading'});
    try {
      const data = await getProductionCrmConnectionStatus(selectedOrg.id);
      if (mountedRef.current && started === contextRef.current.generation &&
          crmStatusRequest.current === request) setCrmStatus({generation: started, phase: 'ready', data});
    } catch {
      if (mountedRef.current && started === contextRef.current.generation &&
          crmStatusRequest.current === request) setCrmStatus({generation: started, phase: 'error'});
    }
  };

  // Wrapper to include subdomain when connecting FUB + persist consent acknowledgment
  const handleCredentialAdded = async (
    credential: { id: string; appKey: string }, initiatedGeneration: number,
    initiatedProtectedApp?: string,
  ) => {
    const route = classifyCredentialCallback({
      initiatedGeneration, currentGeneration: contextRef.current.generation,
      initiatedProtectedApp, credentialAppKey: credential.appKey,
    });
    if (route === 'ignore' || !mountedRef.current || !widgetReady) {
      showToast({message: 'Credential saved in Rollout; the selection or session changed. Reopen integrations to confirm its binding.', variant: 'info'});
      return;
    }
    if (route === 'ordinary' && !rolloutConnectorAppKeys.includes(credential.appKey)) return;
    if (route === 'inspect_only') {
      if (!protectedContextReady || !selectedOrg?.id ||
          currentInspection?.phase !== 'ready' || inspectionInFlightRef.current === initiatedGeneration) return;
      // Rollout has already performed its external Save. This inspection never
      // calls the legacy Speculo connected/status/consent/activation handlers.
      const orgId = selectedOrg.id;
      inspectionInFlightRef.current = initiatedGeneration;
      setInspection({ generation: initiatedGeneration, phase: 'loading' });
      try {
        const data = await readCurrentInspection({
          orgId, app: initiatedProtectedApp as UatCrmInspection['app_key'],
          isCurrent: () => mountedRef.current && initiatedGeneration === contextRef.current.generation,
          request: () => inspectUatSavedCredential(orgId, credential.id),
        });
        if (data) setInspection({ generation: initiatedGeneration, phase: 'saved', data });
      } catch {
        if (mountedRef.current && initiatedGeneration === contextRef.current.generation) {
          setInspection({ generation: initiatedGeneration, phase: 'error' });
        }
      } finally {
        if (inspectionInFlightRef.current === initiatedGeneration) inspectionInFlightRef.current = null;
      }
      return;
    }
    if (!widgetReady) return;
    // Persist explicit consent before the atomic managed-CRM binding transition.
    if (import.meta.env.VITE_APP_ENV === 'production' &&
        ['cinc', 'lofty', 'sierra-interactive', 'boldtrail'].includes(credential.appKey) && !isSuperAdmin) {
      if (!hasAcknowledgedAI && !onboardingConsentAcknowledged) return;
      await persistCrmConsent({ id: selectedOrg!.id, data: { onboardingData: {
        ...baseOnboardingData, crm_consent_acknowledged: true,
        crm_consent_acknowledged_at: new Date().toISOString(),
      } } as any });
      if (!mountedRef.current || initiatedGeneration !== contextRef.current.generation) return;
    }
    setDidDisconnectFub(false);
    const result = await baseHandleCredentialAdded({
      ...credential,
      subdomain: credential.appKey === 'follow-up-boss' ? fubSubdomain : undefined,
    });
    if (!mountedRef.current || initiatedGeneration !== contextRef.current.generation) return;
    if (credential.appKey === 'follow-up-boss' && fubConnectionMode === 'shared_account') {
      setLocalSharedParentId(result.id);
    }
    await refreshManagedCrmStatus(credential.appKey, initiatedGeneration);
    // Save consent acknowledgment to organization onboarding data
    if (hasAcknowledgedAI && selectedOrg?.id &&
        !(import.meta.env.VITE_APP_ENV === 'production' &&
          ['cinc', 'lofty', 'sierra-interactive', 'boldtrail'].includes(credential.appKey))) {
      try {
        await updateSettings({
          id: selectedOrg.id,
          data: {
            onboardingData: {
              ...baseOnboardingData,
              crm_consent_acknowledged: true,
              crm_consent_acknowledged_at: new Date().toISOString(),
            },
          } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
        });
      } catch {
        // Non-blocking — consent is tracked but shouldn't break the connection flow
      }
    }
  };

  // Wrapper to hide stages immediately on disconnect
  const handleCredentialDeletedWithHide = async (
    credential: { id: string; appKey: string }, initiatedGeneration: number,
    initiatedProtectedApp?: string,
  ) => {
    if (!mountedRef.current || initiatedGeneration !== contextRef.current.generation || initiatedProtectedApp || !widgetReady) {
      showToast({message: 'Credential changed in Rollout; the selection or session changed. Reopen integrations to confirm its binding.', variant: 'info'});
      return;
    }
    await handleCredentialDeleted(credential);
    if (!mountedRef.current || initiatedGeneration !== contextRef.current.generation) return;
    await refreshManagedCrmStatus(credential.appKey, initiatedGeneration);
    if (credential.appKey === 'follow-up-boss') {
      setDidDisconnectFub(true);
      // Drop the cached health so the owner-key banner clears immediately —
      // a disabled query keeps its last value otherwise.
      queryClient.removeQueries({ queryKey: ['integrationHealth', selectedOrg?.id] });
    }
  };

  // Combined loading state for FUB operations
  const isFubLoading = isConnecting || isDisconnecting;

  // Twilio is configured if a phone number is set (we may not have SID/token stored)
  const isTwilioConfigured = () => {
    return Boolean(selectedOrg?.phoneNumbers?.outbound || selectedOrg?.phoneNumbers?.inbound);
  };

  // Migrating to Rollout — commented out with CINC/Brivity sections
  // const isCRMConfigured = () => {
  //   return Boolean(selectedOrg?.externalClient);
  // };

  // const isCINCConfigured = () => {
  //   return Boolean(selectedOrg?.cincSetup?.connected);
  // };

  const [oauthVerified, setOauthVerified] = useState(false);

  // Check OAuth status from the API on modal open (org.oauthSetup may be stale/null in GQL)
  useEffect(() => {
    if (!isOpen || !selectedOrg?.id) return;
    setOauthVerified(false);
    getOAuthClientInfo(selectedOrg.id)
      .then((info) => { if (info?.clientId) setOauthVerified(true); })
      .catch(() => {});
  }, [isOpen, selectedOrg?.id]);

  const isOAuthConfigured = () => {
    return oauthVerified || Boolean(
      selectedOrg?.oauthSetup?.created_at ||
      selectedOrg?.oauthSetup?.createdAt ||
      selectedOrg?.oauthSetup?.clientId ||
      selectedOrg?.oauthSetup?.client_id
    );
  };

  // Handle success from child modals
  const handleSuccess = () => {
    onSuccess?.();
  };

  // Disconnect a legacy (API-key) FUB integration. Clears the same fields the
  // removed legacy modal did; crmApiKeyHash is what gates isLegacyFubConnected,
  // and updateSettings' Firestore dual-write is what actually clears it.
  const handleDisconnectLegacyFub = async () => {
    const orgId = selectedOrg?.id;
    if (!orgId) return;
    setIsDisconnectingLegacy(true);
    try {
      const data: Record<string, unknown> = {
        fubSetup: {},
        crmApiKey: '',
        crmApiKeyHash: '',
      };
      if ((selectedOrg as { connectedCRM?: string }).connectedCRM === 'followupboss') {
        data.connectedCRM = '';
      }
      await updateSettings({
        id: orgId,
        data: data as Partial<Organization>,
      });
      showToast({ message: 'Legacy Follow Up Boss disconnected', variant: 'success' });
      setShowConfirmLegacyDisconnect(false);
    } catch (error) {
      console.error('Failed to disconnect legacy Follow Up Boss:', error);
      showToast({
        message: 'Failed to disconnect Legacy Follow Up Boss. Please try again.',
        variant: 'error',
      });
    } finally {
      setIsDisconnectingLegacy(false);
    }
  };

  const isSavingSubdomain = isSavingSubdomainViaApi;

  // Save subdomain via Remi API
  const handleSaveSubdomain = async () => {
    if (!selectedOrg?.id) return;

    const validation = validateSubdomain(fubSubdomain);
    if (!validation.isValid) {
      showToast({
        message: validation.error || 'Invalid subdomain format',
        variant: 'error',
      });
      return;
    }

    try {
      // Canonical store for FUB subdomain is `fubSetup.subdomain` — that's the
      // column the backend reads (see remi-api onboarding.py secrets-build).
      // Writing here also keeps us out of `rollout_config`, where the Rollout
      // webhook owns the credentials list and our writes risk racing with it.
      await updateSettings({
        id: selectedOrg.id,
        data: {
          fubSetup: {
            ...(selectedOrg.fubSetup ?? {}),
            subdomain: fubSubdomain?.toLowerCase() || null,
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      });
      setIsEditingSubdomain(false);
    } catch (error) {
      console.error('Failed to save subdomain:', error);
      showToast({
        message: 'Failed to save subdomain. Please try again.',
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <Modal.Header>
          <Typography variant="h3">{effectiveCrmFilter ? `${ROLLOUT_CRMS[effectiveCrmFilter].label} Setup` : 'Integrations'}</Typography>
        </Modal.Header>
        <Modal.Body className="pb-8">
          <AdminLockOverlay locked={!isAdmin} subject="Integrations">
          <div className="space-y-3">
            {!restricted && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <p className="text-gray-600 dark:text-gray-400">Configure integrations with third-party services.</p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isOAuthConfigured() && (
                    <div className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs rounded-full whitespace-nowrap">
                      Active
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsOAuthModalOpen(true)}
                    disabled={!isSuperAdmin}
                    className="flex items-center gap-2"
                  >
                    <Code className="w-4 h-4" />
                    <span className="hidden sm:inline">OAuth</span>
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-4">
              {/* Twilio Integration - Super Admin Only */}
              {isSuperAdmin && !hideTwilio && (
                <div className="border border-gray-300 dark:border-white/15 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-full flex-shrink-0">
                        <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <Typography variant="h5" className="font-semibold">
                          Twilio
                        </Typography>
                        {isTwilioConfigured() && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {formatPhoneNumber(selectedOrg?.phoneNumbers?.outbound || selectedOrg?.phoneNumbers?.inbound)}
                            {' \u00b7 '}Enter credentials to update phone number
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isTwilioConfigured() && (
                        <div className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs rounded-full whitespace-nowrap">
                          Configured
                        </div>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsTwilioModalOpen(true)}
                        className="flex items-center gap-1"
                      >
                        <Edit className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          {isTwilioConfigured() ? 'Update' : 'Setup'}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* CRM Integrations - Rollout OAuth */}
              <div
                className={`border border-gray-300 dark:border-white/15 rounded-lg p-4 ${
                  isLegacyFubConnected ? 'bg-gray-50 dark:bg-white/5' : ''
                }`}
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-full flex-shrink-0">
                      <RotateCw className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <Typography variant="h5" className="font-semibold">
                        CRM Integrations
                      </Typography>
                      {isLegacyFubConnected && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Please disconnect Legacy FUB first
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Show blocked message when legacy FUB is connected */}
                {isLegacyFubConnected ? (
                  <div className="py-4 px-3 bg-gray-100 dark:bg-white/5 rounded border border-gray-200 dark:border-white/10">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      CRM integrations are not available while the Legacy FUB integration
                      is active. Disconnect it below to use this version.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowConfirmLegacyDisconnect(true)}
                      disabled={isDisconnectingLegacy}
                      isLoading={isDisconnectingLegacy}
                      className="mt-3 flex items-center gap-1"
                    >
                      <X className="w-4 h-4" />
                      Disconnect Legacy FUB
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Pro tip about speculo_sync tag - only show when FUB not connected */}
                    {!isFubConnected && (
                      <div className="mb-3">
                        <TipBox>
                          <strong>Pro tip:</strong> Before connecting your CRM, we highly recommend
                          applying the{' '}
                          <code className="bg-yellow-200 dark:bg-yellow-800/40 px-1 rounded">speculo_sync</code> tag to
                          the contacts you want Remi (AI) to engage with to help speed up the
                          initial sync process especially for large databases. This tag does not
                          trigger any communication. You can still move forward without applying
                          the tag. Additional contacts can be synced over at any time after the
                          integration is established.
                        </TipBox>
                      </div>
                    )}

                    {/* Sync Status Banners - only show if FUB is connected */}
                    {isFubConnected && syncStatus && isSyncing && (
                      <RolloutSyncStatusBanner syncStatus={syncStatus} />
                    )}
                    {isFubConnected && syncStatus && !isSyncing && (
                      <RolloutSyncResults syncStatus={syncStatus} />
                    )}

                    {/* Owner-account warning — webhook sync requires an owner-level FUB key */}
                    {isFubConnected && isNonOwnerKey && (
                      <SpeculoAlert variant="warning" className="mb-3">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{NON_OWNER_KEY_WARNING}</AlertDescription>
                      </SpeculoAlert>
                    )}

                    {/* Loading state */}
                    {tokenLoading && (
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <RotateCw className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Loading...</span>
                      </div>
                    )}

                    {/* Error state */}
                    {tokenError && (
                      <div className="text-red-500 text-sm">
                        Failed to load CRM integrations
                      </div>
                    )}

                    {/* FUB account structure — only while the Follow Up Boss connector is open */}
                    {showFubAccountStructure && (
                      <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-white/10 dark:bg-white/5">
                        <Typography variant="small" className="font-semibold text-gray-700 dark:text-gray-300">
                          Follow Up Boss account structure
                        </Typography>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                          <button
                            type="button"
                            onClick={() => setFubConnectionMode('single_org')}
                            className={`rounded-md border p-3 text-left text-sm ${
                              fubConnectionMode === 'single_org'
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900'
                            }`}
                          >
                            <span className="block font-medium">One Speculo organization</span>
                            <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">Use the full FUB account for this organization.</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setFubConnectionMode('shared_account')}
                            className={`rounded-md border p-3 text-left text-sm ${
                              fubConnectionMode === 'shared_account'
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900'
                            }`}
                          >
                            <span className="block font-medium">Shared account with Teams</span>
                            <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">Map FUB Teams to separate Speculo organizations or locations.</span>
                          </button>
                        </div>
                        {isSharedFubMode && (
                          <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                            Connect with the FUB Owner API key. Sync remains off until Team mappings pass a dry run and are activated.
                          </p>
                        )}
                      </div>
                    )}
                    {protectedApp && (
                      <div className="mb-3 rounded-md border border-amber-300 p-3 text-sm" role="region" aria-label="UAT CRM inspection">
                        <p>UAT test account: not qualified for import or outreach. No Speculo connection is installed by this inspection.</p>
                        <p>Saving in Rollout creates a credential and may grant CRM access; review the provider terms before proceeding.</p>
                        {protectedApp === 'lofty' && protectedContextReady && (
                          <Button type="button" variant="outline" size="sm"
                            disabled={currentExistingLoftyInspection?.phase === 'loading'} onClick={inspectExistingLofty}>
                            Inspect existing Lofty credential
                          </Button>
                        )}
                        {protectedApp === 'lofty' && currentExistingLoftyInspection?.phase === 'loading' &&
                          <p>Inspecting existing credential metadata…</p>}
                        {protectedApp === 'lofty' && currentExistingLoftyInspection?.phase === 'error' &&
                          <p>Existing credential inspection unavailable. Not qualified or installed.</p>}
                        {protectedApp === 'lofty' && currentExistingLoftyInspection?.phase === 'ready' &&
                          currentExistingLoftyInspection.data && (
                          <div>
                            <p>Existing saved Lofty credential metadata inspected. Not qualified or installed.</p>
                            <p>Plans: {currentExistingLoftyInspection.data.metadata.plans.map((plan) =>
                              `${plan.plan ?? 'unnamed'} (${plan.auth_status})`).join(', ') || 'none'}</p>
                            <p>Read capabilities: {currentExistingLoftyInspection.data.metadata.read_capabilities
                              .filter((capability) => capability.list_supported)
                              .map((capability) => capability.entity).join(', ') || 'none'}</p>
                            <p>Profile field shapes: {currentExistingLoftyInspection.data.metadata.profile_paths
                              .map((field) => `${field.path}: ${field.type}`).join(', ') || 'none'}</p>
                          </div>
                        )}
                        {protectedApp === 'lofty' && protectedContextReady && (
                          <div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {currentLoftyPersonRead() && (
                                <Button type="button" variant="outline" size="sm"
                                  disabled={currentLoftyPerson?.phase === 'loading' ||
                                    currentLoftyStatus?.phase === 'loading' || currentLoftyInstallPhase?.phase === 'loading'}
                                  onClick={inspectExactLoftyPerson}>
                                  Inspect exact synthetic person
                                </Button>
                              )}
                              <Button type="button" variant="outline" size="sm"
                                disabled={currentLoftyPerson?.phase === 'loading' ||
                                  currentLoftyStatus?.phase === 'loading' || currentLoftyInstallPhase?.phase === 'loading'}
                                onClick={readLoftyDisabledStatus}>
                                Check disabled binding status
                              </Button>
                            </div>
                            {currentLoftyPerson?.phase === 'loading' && <p>Reading exact synthetic person…</p>}
                            {currentLoftyPerson?.phase === 'error' &&
                              <p>Exact person read unavailable. Account and import remain unqualified.</p>}
                            {currentLoftyPerson?.phase === 'ready' && currentLoftyPerson.data &&
                              <p>Exact fixture match: {currentLoftyPerson.data.person_id_match &&
                                currentLoftyPerson.data.synthetic_email && currentLoftyPerson.data.phone_absent &&
                                currentLoftyPerson.data.names_present ? 'yes' : 'no'}.
                                Provider account, import and outreach remain unqualified.</p>}
                            {currentLoftyStatus?.phase === 'loading' && <p>Checking disabled binding…</p>}
                            {currentLoftyStatus?.phase === 'error' &&
                              <p>Disabled binding status unavailable; outcome requires readback.</p>}
                            {currentLoftyStatus?.phase === 'ready' && currentLoftyStatus.data &&
                              <p>Binding: {currentLoftyStatus.data.status === 'credential_bound_disabled'
                                ? 'bound for UAT inspection only' : 'not installed'}.
                                Provider account, import and outreach remain unqualified.</p>}
                            {currentLoftyStatus?.phase === 'ready' &&
                              currentLoftyStatus.data?.install_admitted !== true &&
                              <p>Install disabled binding is unavailable until the UAT server safety gate is enabled.</p>}
                            {currentLoftyStatus?.phase === 'ready' && (
                              <Button type="button" variant="outline" size="sm"
                                disabled={!currentLoftyAction() ||
                                  currentLoftyStatus.data?.install_admitted !== true ||
                                  currentLoftyStatus.data.status !== 'not_installed' ||
                                  currentLoftyInstallPhase?.phase === 'loading'}
                                onClick={installLoftyDisabled}>
                                Install disabled binding
                              </Button>
                            )}
                            {currentLoftyInstallPhase?.phase === 'loading' &&
                              <p>Installing disabled binding; awaiting readback…</p>}
                            {currentLoftyInstallPhase?.phase === 'error' &&
                              <p>Disabled binding outcome unavailable. Reinspect status before another action.</p>}
                          </div>
                        )}
                        {savedCredentialOrgId && (
                          <div className="mt-2 space-y-2">
                            <Button type="button" variant="outline" size="sm"
                              disabled={currentSavedStatus?.phase === 'loading' ||
                                currentSavedCandidate?.phase === 'loading' ||
                                currentSavedInstallPhase?.phase === 'loading'}
                              onClick={readSavedCredentialStatus}>
                              Check saved credential status
                            </Button>
                            {currentSavedStatus?.phase === 'loading' && <p>Checking saved credential status…</p>}
                            {currentSavedStatus?.phase === 'error' &&
                              <p>Saved credential status unavailable; outcome requires readback.</p>}
                            {currentSavedStatus?.phase === 'ready' && currentSavedStatus.data && (
                              <div className="space-y-2">
                                <p>Binding: {currentSavedStatus.data.status === 'not_saved' ? 'not saved' :
                                  currentSavedStatus.data.status === 'credential_bound_disabled'
                                    ? 'bound for UAT inspection only' : 'not installed'}.
                                  Provider account, import and outreach remain unqualified.</p>
                                {savedStatusIsCurrent() &&
                                  <p>Saved credential metadata: plan {currentSavedStatus.data.plan},
                                    auth type {currentSavedStatus.data.auth_type}. Native account is not qualified.</p>}
                                {savedStatusIsCurrent() && (
                                  <Button type="button" variant="outline" size="sm"
                                    disabled={currentSavedCandidate?.phase === 'loading' ||
                                      currentSavedInstallPhase?.phase === 'loading'}
                                    onClick={readSavedCredentialCandidate}>
                                    Inspect exact candidate person
                                  </Button>
                                )}
                                {currentSavedStatus.data.install_admitted !== true &&
                                  <p>Install disabled binding is unavailable until the UAT server safety gate is enabled.</p>}
                                <Button type="button" variant="outline" size="sm"
                                  disabled={!savedStatusIsCurrent() ||
                                    currentSavedStatus.data.status !== 'not_installed' ||
                                    currentSavedStatus.data.install_admitted !== true ||
                                    currentSavedCandidate?.phase === 'loading' ||
                                    currentSavedInstallPhase?.phase === 'loading'}
                                  onClick={installSavedCredentialDisabled}>
                                  Install disabled binding
                                </Button>
                              </div>
                            )}
                            {currentSavedCandidate?.phase === 'loading' && <p>Reading exact candidate person…</p>}
                            {currentSavedCandidate?.phase === 'error' &&
                              <p>Candidate read unavailable. Account and import remain unqualified.</p>}
                            {currentSavedCandidate?.phase === 'ready' && currentSavedCandidate.data &&
                              <p>Candidate fixture match: {currentSavedCandidate.data.candidate_id_match &&
                                currentSavedCandidate.data.synthetic_shape_match ? 'yes' : 'no'}.
                                Native account, import and outreach remain unqualified.</p>}
                            {currentSavedInstallPhase?.phase === 'loading' &&
                              <p>Installing disabled binding; awaiting readback…</p>}
                            {currentSavedInstallPhase?.phase === 'error' &&
                              <p>Disabled binding outcome unavailable. Reinspect status before another action.</p>}
                          </div>
                        )}
                        {protectedContextReady ? (
                          <Button type="button" variant="outline" size="sm"
                            disabled={currentInspection?.phase === 'loading'} onClick={inspectProtectedConnector}>
                            Inspect connector before Save
                          </Button>
                        ) : <p>Inspection unavailable until the current inactive organization detail and super-admin session match.</p>}
                        {currentInspection?.phase === 'loading' && <p>Inspecting metadata…</p>}
                        {currentInspection?.phase === 'error' && <p>Inspection unavailable. Not qualified or installed.</p>}
                        {currentInspection?.data && (
                          <div>
                            <p>Read-only metadata. Not qualified or installed.</p>
                            <p>Plans: {currentInspection.data.metadata.plans.map((plan) =>
                              `${plan.plan ?? 'unnamed'} (${plan.auth_status})`).join(', ') || 'none'}</p>
                            <p>Read capabilities: {currentInspection.data.metadata.read_capabilities
                              .filter((capability) => capability.list_supported)
                              .map((capability) => capability.entity).join(', ') || 'none'}</p>
                            <p>Profile field shapes: {currentInspection.data.metadata.profile_paths
                              .map((field) => `${field.path}: ${field.type}`).join(', ') || 'none'}</p>
                            {currentInspection.phase === 'saved' &&
                              <p>Credential metadata inspected. Speculo connection remains not installed.</p>}
                          </div>
                        )}
                      </div>
                    )}
                    {showManagedSyncDirection &&
                      <div className="mb-3">
                        <p className="block text-sm font-medium">
                          CRM record sync direction
                        </p>
                        <p>New managed CRM connections: Bidirectional CRM records</p>
                        <p className="text-xs">Bidirectional sync can update CRM records. This does not enable calls, texts, or emails.</p>
                        {currentManagedCrm?.status === 'connected' &&
                          <p role="status">Current CRM sync: {currentManagedCrm.sync_direction === 'bidirectional'
                            ? 'Bidirectional' : currentManagedCrm.sync_direction === 'inbound'
                              ? 'Inbound only' : 'Direction unavailable'}</p>}
                        {currentManagedCrm?.status === 'sync_paused' &&
                          <p role="status">Current CRM sync is paused. Stored direction: {currentManagedCrm.sync_direction ?? 'unavailable'}.</p>}
                        {currentManagedCrm?.status === 'pending_setup' &&
                          <p role="status">Current CRM connection is pending setup; bidirectional sync is not active.</p>}
                      </div>}

                    {!widgetReady && <p role="status">Waiting for the current organization and signed-in session before loading CRM integrations.</p>}
                    {widgetReady && rolloutToken && (!protectedApp || (protectedContextReady && currentInspection?.phase === 'ready')) && (
                      <div
                        className={`relative rollout-container ${showConsentGate ? 'pointer-events-none opacity-60' : ''}`}
                        onClickCapture={trackRolloutConnector}
                      >
                        {/* Loading overlay during connect/disconnect */}
                        {isFubLoading && (
                          <div className="absolute inset-0 bg-white/70 dark:bg-black/50 flex items-center justify-center z-10 rounded">
                            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                              <RotateCw className="w-4 h-4 animate-spin" />
                              <span className="text-sm">
                                {isConnecting ? 'Connecting...' : 'Disconnecting...'}
                              </span>
                            </div>
                          </div>
                        )}
                        <RolloutLinkProvider token={rolloutToken}>
                          <CredentialsManager
                            apiCategories={{ crm: true }}
                            entitiesToSync={protectedApp ? {} : { notes: true, tasks: true }}
                            onCredentialAdded={(credential) => handleCredentialAdded(credential, generation, protectedApp)}
                            onCredentialDeleted={(credential) => handleCredentialDeletedWithHide(credential, generation, protectedApp)}
                            shouldRenderConnector={(connector: { appKey?: string }) => {
                              if (protectedApp) return connector.appKey === protectedApp;
                              return Boolean(connector.appKey && rolloutConnectorAppKeys.includes(connector.appKey));
                            }}
                          />
                        </RolloutLinkProvider>
                      </div>
                    )}

                    {/* CRMs connected through Speculo's Connect form, as tiles like Rollout's list */}
                    {showManualCrmTiles && selectedOrg && (
                      <div
                        className={`mt-4 grid grid-cols-[repeat(auto-fill,minmax(130px,1fr))] gap-4 ${showConsentGate ? 'pointer-events-none opacity-60' : ''}`}
                      >
                        {MANUAL_CONNECT_CRM_IDS.filter(
                          (crm) => !isManualCrmConnectedViaRollout(crm)
                        ).map((crm) => {
                          const request = getCrmConnectRequest(selectedOrg.onboardingData, crm);
                          return (
                            <button
                              key={crm}
                              type="button"
                              onClick={() => setManualConnectCrm(crm)}
                              className="relative flex flex-col justify-center aspect-square p-3 rounded-lg border border-black/10 dark:border-white/10 shadow-[0_0_6px_#0000000f] hover:shadow-[0_0_10px_#00000026] transition-shadow text-center"
                            >
                              <img
                                src={MANUAL_CONNECT_CRMS[crm].icon}
                                alt=""
                                className="block mx-auto w-12 aspect-square"
                              />
                              <span className="mt-3.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {ROLLOUT_CRMS[crm].label}
                              </span>
                              {request && (
                                <span className="mt-1 text-xs text-green-700 dark:text-green-400">
                                  Submitted {new Date(request.submitted_at).toLocaleDateString()}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {showManualCrmTiles && selectedOrg && (
                      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                        {CRM_TRADEMARK_NOTICE}
                      </p>
                    )}

                    {isSuperAdmin && isSharedFubMode && sharedParentConnectionId && (
                      <SharedFubAccountSetup
                        connectionId={sharedParentConnectionId}
                        onActivated={onSuccess}
                      />
                    )}

                    {/* Consent checkbox — hidden for super admins so they can't
                        accidentally accept the contact-consent declaration on
                        behalf of the org. */}
                    {showConsentGate && (
                      <div className="mt-3 p-4 border border-amber-200 dark:border-amber-500/30 rounded-lg bg-amber-50 dark:bg-amber-900/10">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="crm-ai-acknowledgement"
                            checked={hasAcknowledgedAI}
                            onChange={async (e) => {
                              const checked = e.target.checked;
                              setHasAcknowledgedAI(checked);
                              if (!checked || !selectedOrg?.id) return;
                              try {
                                await updateSettings({
                                  id: selectedOrg.id,
                                  data: {
                                    onboardingData: {
                                      ...baseOnboardingData,
                                      crm_consent_acknowledged: true,
                                      crm_consent_acknowledged_at: new Date().toISOString(),
                                    },
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                  } as any,
                                });
                              } catch {
                                // Non-blocking — revert local state so the user can retry
                                setHasAcknowledgedAI(false);
                              }
                            }}
                          />
                          <label htmlFor="crm-ai-acknowledgement" className="text-xs text-foreground/80 leading-relaxed cursor-pointer whitespace-pre-line">
                            {aiAcknowledgementCopy}
                          </label>
                        </div>
                      </div>
                    )}

                    {/* FUB Stages Configuration - shown when FUB is connected (hide for non-FUB filters) */}
                    {isFubConnected && !isSharedFubMode && (!effectiveCrmFilter || effectiveCrmFilter === 'fub') && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-white/10">
                        <Typography variant="small" className="font-semibold text-gray-500 dark:text-gray-400 mb-2">
                          Follow Up Boss
                        </Typography>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Settings className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              Stages:{' '}
                              {isLoadingFubStages
                                ? 'Loading...'
                                : enabledStagesCount > 0
                                  ? `${enabledStagesCount} configured`
                                  : 'Not configured'}
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsStagesModalOpen(true)}
                            className="flex items-center gap-1 h-8 px-4"
                          >
                            <Settings className="w-4 h-4" />
                            <span className="hidden sm:inline">Configure Stages</span>
                          </Button>
                        </div>

                        {/* FUB Subdomain Configuration */}
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-2">
                            <Link className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            {isEditingSubdomain ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  value={fubSubdomain}
                                  onChange={(e) => setFubSubdomain(e.target.value.trim().toLowerCase())}
                                  placeholder="yourcompany"
                                  className="w-32"
                                  autoFocus
                                />
                                <span className="text-xs text-gray-500 dark:text-gray-400">.followupboss.com</span>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                Subdomain:{' '}
                                {fubSubdomainPersisted
                                  ? `${fubSubdomainPersisted}.followupboss.com`
                                  : 'Not configured'}
                              </span>
                            )}
                          </div>
                          {isEditingSubdomain ? (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleSaveSubdomain}
                                disabled={isSavingSubdomain}
                                className="flex items-center gap-1 h-8 px-2"
                              >
                                {isSavingSubdomain ? (
                                  <RotateCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Check className="w-4 h-4 text-green-600" />
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setFubSubdomain(fubSubdomainPersisted);
                                  setIsEditingSubdomain(false);
                                }}
                                disabled={isSavingSubdomain}
                                className="flex items-center gap-1 h-8 px-2"
                              >
                                <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setIsEditingSubdomain(true)}
                              className="flex items-center gap-1 h-8 px-4"
                            >
                              <Edit className="w-4 h-4" />
                              <span className="hidden sm:inline">Edit</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

          </div>
          </AdminLockOverlay>
        </Modal.Body>
      </Modal>

      {/* Child Modals */}
      <TwilioModal
        isOpen={isTwilioModalOpen}
        onClose={() => setIsTwilioModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />

      <CRMModal
        isOpen={isCRMModalOpen}
        onClose={() => setIsCRMModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />

      {/* Website Integration Instructions Modal */}
      <WebsiteIntegrationModal
        isOpen={isIntegrationModalOpen}
        onClose={() => setIsIntegrationModalOpen(false)}
        orgId={selectedOrg?.id}
      />

      <CINCModal
        isOpen={isCINCModalOpen}
        onClose={() => setIsCINCModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={handleSuccess}
      />

      <SpeculoOAuthModal
        isOpen={isOAuthModalOpen}
        onClose={() => setIsOAuthModalOpen(false)}
        selectedOrg={selectedOrg}
        onSuccess={() => {
          setOauthVerified(true);
          handleSuccess();
        }}
      />

      {/* FUB Stages Configuration Modal */}
      <FubStagesModal
        isOpen={isStagesModalOpen}
        onClose={() => setIsStagesModalOpen(false)}
        orgId={fubConnectionId}
        onSuccess={handleSuccess}
      />

      {/* Speculo Connect form for CRMs the team connects by hand */}
      {manualConnectCrm && selectedOrg && (
        <ManualCrmConnectModal
          crm={manualConnectCrm}
          organization={selectedOrg}
          source="client_admin"
          onClose={() => setManualConnectCrm(null)}
          onSubmitted={() => onSuccess?.()}
        />
      )}

      {/* Legacy FUB disconnect confirmation */}
      <ConfirmationDialog
        isOpen={showConfirmLegacyDisconnect}
        onClose={() => setShowConfirmLegacyDisconnect(false)}
        onConfirm={handleDisconnectLegacyFub}
        title="Disconnect Legacy Follow Up Boss?"
        message="This removes the legacy API-key Follow Up Boss integration so you can connect a CRM with the current version. You can reconnect afterward."
        confirmLabel="Disconnect"
        variant="danger"
        isLoading={isDisconnectingLegacy}
      />
    </>
  );
};


export const IntegrationsModal: React.FC<IntegrationsModalProps> = (props) => {
  const orgId = props.selectedOrgId ?? props.selectedOrg?.id;
  if (import.meta.env.VITE_APP_ENV === 'production' && isProductionTestCrmOrg(orgId)) {
    if (orgId === '70299cd6-a817-4eeb-a333-df660b1f204e' ||
        orgId === '816d1e20-f38b-466e-96ec-46bb7d0f404c' ||
        orgId === '6e5adb4f-c2b4-459b-8c40-f41bb801bbf2') {
      const app = PRODUCTION_TEST_CRMS[orgId].app;
      const crmFilter: RolloutCrmId = app === 'sierra-interactive' ? 'sierra_interactive' : app;
      return <OrdinaryIntegrationsModal {...props} crmFilter={crmFilter} />;
    }
    return <ExistingProductionCrmBinding isOpen={props.isOpen} onClose={props.onClose}
      orgId={orgId} selectedOrgId={props.selectedOrgId} currentRowId={props.currentRowId}
      freshDetailId={props.freshDetailId} freshDetailLoading={props.freshDetailLoading}
      onSuccess={props.onSuccess} />;
  }
  return <OrdinaryIntegrationsModal {...props} />;
};
